import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { MODAL_DIALOG_WIDTH } from 'modules/core/constants';
import { Card } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FormRow, MoneyLabel, Panel } from 'modules/core/components';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import { useTranslation } from 'react-i18next';
import { ColumnWidth, DealSourceProvider } from 'modules/core/enums';
import { getUser } from 'store/selectors/auth.selector';
import { IProbabilityEstimatorForm } from 'modules/ProbabilityEstimator/types';
import { TextCollapser } from 'components/common/TextCollapser/TextCollapser';
import { produce } from 'immer';
import { LOAN_VARIABLES_FORM_PREFIX } from 'components/Modals/LoanVariablesDialog/consts/fieldNames.const';
import { APPROVED_LOAN_TERMS_FIELD_NAMES } from 'modules/RACAffordabilityEngine/const/fieldNames.const';
import { IDealSourceData } from 'modules/core/types/dealSource';
import { getNormalizedDirty } from 'components/Modals/utils';

import { MONTHLY_INCOME_FIELDS } from './consts/fieldConfig.const';
import { RAC_MONTHLY_INCOME_FIELDS_NAMES, MONTHLY_INCOME_FORM_PREFIX } from './consts/fieldNames.const';
import { COMMON_MONTHLY_INCOME_MODAL } from '../const/fieldNames.const';
import {
  CoApplicantHint,
  TotalIncomePanel,
  MonthlyIncomeTitleContainer,
  MonthlyIncomeTitle,
  MonthlyIncomeSubLabel,
} from './styles';
import { ILendersMonthlyIncome } from './consts/types';

const { Body } = Card;

interface INetDownPaymentModal {
  show: boolean;
  onHide: VoidFunction;
  modalName: string;
  totalName: string;
  setFormHistory?: React.Dispatch<React.SetStateAction<any[]>>;
  initialValues: ILendersMonthlyIncome;
  verticallyCentered?: boolean;
  isLender?: boolean;
  setValueDelegate?: Function;
  isDealSourceData?: boolean;
  dealSourceData?: IDealSourceData;
}

export const LendersMonthlyIncomeModal: FC<INetDownPaymentModal> = ({
  show,
  onHide,
  setFormHistory,
  modalName,
  totalName,
  initialValues,
  setValueDelegate,
  verticallyCentered = false,
  isLender = false,
  isDealSourceData = false,
  dealSourceData,
}) => {
  const { t } = useTranslation();
  const user = useSelector(getUser);
  const isDealerTrack = user?.dealSource === DealSourceProvider.DEALER_TRACK;
  const lockCoApplicantIncome = isDealSourceData && !isDealerTrack;
  const { setValue: commonSetValue, watch: commonWatch } = useFormContext<IProbabilityEstimatorForm>();
  const loanTermFormValues = commonWatch(LOAN_VARIABLES_FORM_PREFIX) as any;

  const formMethods = useForm({
    mode: 'onSubmit',
    shouldUnregister: false,
    defaultValues: initialValues,
  });
  const {
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    formState: { isDirty },
  } = formMethods;
  const { totalIncome } = watch(MONTHLY_INCOME_FORM_PREFIX);

  const normalizedDirty = getNormalizedDirty(formMethods, initialValues);

  const handleSubmitModal = useCallback(() => {
    const values = getValues();

    commonSetValue(modalName, values);
    commonSetValue(totalName, Math.round(values.income.totalIncome));

    if (setValueDelegate) {
      setValueDelegate(COMMON_MONTHLY_INCOME_MODAL, values);
      setValueDelegate(APPROVED_LOAN_TERMS_FIELD_NAMES.GROSS_MONTHLY_INCOME, Math.round(values.income.totalIncome));
    }

    if (setFormHistory && isDirty) {
      const data = {
        loanVariablesForm: {
          ...loanTermFormValues,
          monthlyIncomeForm: values,
          monthlyIncome: values.income.totalIncome,
        },
      };

      setFormHistory((prevHistory) =>
        produce(prevHistory, (draft) => {
          draft.push(data);
        }),
      );
    }

    onHide();
  }, [commonSetValue, getValues, isDirty, loanTermFormValues, modalName, onHide, setFormHistory, totalName, setValueDelegate]); // eslint-disable-line

  const {
    coApplicantMonthlyIncome,
    coApplicantOtherMonthlyIncome,
    primaryMonthlyIncome,
    primaryOtherMonthlyIncome,
  } = getValues().income;

  const netDownPaymentFields = useMemo(() => {
    const requiredFields = [RAC_MONTHLY_INCOME_FIELDS_NAMES.PRIMARY_MONTHLY_INCOME];
    const monthlyIncomeFields = [
      {
        incomeField: 'coApplicant_monthly_income',
        fieldName: RAC_MONTHLY_INCOME_FIELDS_NAMES.CO_APPLICANT_MONTHLY_INCOME,
        labelKey: 'components.racIncomeCalculator.labels.coApplicantMonthlyIncomeWithNumber',
      },
      {
        incomeField: 'coApplicant_other_monthly_income',
        fieldName: RAC_MONTHLY_INCOME_FIELDS_NAMES.CO_APPLICANT_OTHER_MONTHLY_INCOME,
        labelKey: 'components.racIncomeCalculator.labels.coApplicantOtherMonthlyIncomeWithNumber',
      },
    ];

    return MONTHLY_INCOME_FIELDS.map(({ name, label, component: Component, controlProps }) => {
      monthlyIncomeFields.forEach(({ incomeField, fieldName, labelKey }) => {
        const incomeValue = dealSourceData?.income?.[incomeField as keyof IDealSourceData['income']];

        if (lockCoApplicantIncome && !incomeValue && name === fieldName) {
          label = t(labelKey);
          controlProps = { ...controlProps, disabled: true };
        }
      });

      return (
        <FormRow
          key={name}
          fieldWidth={ColumnWidth.M}
          label={label}
          name={name}
          {...(requiredFields.includes(name) && { required: true })}
          control={<Component name={name} {...controlProps} />}
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, lockCoApplicantIncome, dealSourceData?.income]);

  const coApplicantHint = useMemo(() => {
    if (
      lockCoApplicantIncome &&
      (!dealSourceData?.income?.coApplicant_monthly_income || !dealSourceData?.income?.coApplicant_other_monthly_income)
    ) {
      return <CoApplicantHint>{t('components.racIncomeCalculator.labels.coApplicantCannotBeChanged')}</CoApplicantHint>;
    }

    return null;
  }, [
    t,
    lockCoApplicantIncome,
    dealSourceData?.income?.coApplicant_monthly_income,
    dealSourceData?.income?.coApplicant_other_monthly_income,
  ]);

  const calculateTotalIncome = useCallback(() => {
    const totalIncome =
      (coApplicantMonthlyIncome || 0) +
      (coApplicantOtherMonthlyIncome || 0) +
      (primaryMonthlyIncome || 0) +
      (primaryOtherMonthlyIncome || 0);

    setValue(RAC_MONTHLY_INCOME_FIELDS_NAMES.TOTAL_INCOME, totalIncome);
  }, [
    coApplicantMonthlyIncome,
    coApplicantOtherMonthlyIncome,
    primaryMonthlyIncome,
    primaryOtherMonthlyIncome,
    setValue,
  ]);

  useEffect(() => {
    reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialValues.income.totalIncome,
    initialValues.income.primaryMonthlyIncome,
    initialValues.income.primaryOtherMonthlyIncome,
    initialValues.income.coApplicantMonthlyIncome,
    initialValues.income.coApplicantOtherMonthlyIncome,
  ]);

  useEffect(() => {
    calculateTotalIncome();
  }, [calculateTotalIncome]);

  return (
    <ModalDialog
      title={t('components.racIncomeCalculator.title')}
      submitButtonTitle={t('common.buttons.save')}
      cancelButtonTitle={t('common.buttons.cancel')}
      show={show}
      onHide={onHide}
      onSubmitModal={handleSubmit(handleSubmitModal)}
      modalSize={MODAL_DIALOG_WIDTH.SM}
      verticallyCentered={verticallyCentered}
      upperCaseTitle
      isDirty={normalizedDirty}
    >
      <Body className="monthly-income-modal">
        <FormProvider {...formMethods}>
          <TotalIncomePanel>
            <FormRow
              name={RAC_MONTHLY_INCOME_FIELDS_NAMES.TOTAL_INCOME}
              label={t('components.racIncomeCalculator.labels.totalIncome')}
              subLabel={t('components.incomeCalculator.labels.perMonth')}
              fieldWidth={ColumnWidth.M}
              control={
                <MoneyLabel isInteger isColored>
                  {totalIncome}
                </MoneyLabel>
              }
            />
          </TotalIncomePanel>

          <Panel>
            <MonthlyIncomeTitleContainer>
              <MonthlyIncomeTitle>{t('components.racIncomeCalculator.labels.monthlyIncome')}</MonthlyIncomeTitle>
              <MonthlyIncomeSubLabel>
                {t('components.racIncomeCalculator.labels.monthlyIncomeBeforeTaxes')}
              </MonthlyIncomeSubLabel>
            </MonthlyIncomeTitleContainer>
            {netDownPaymentFields}
            {coApplicantHint}
          </Panel>
          {!isLender && (
            <TextCollapser
              label={t('components.incomeCalculator.lenderIncomePolicy.label')}
              textKey={'components.incomeCalculator.lenderIncomePolicy.text'}
            />
          )}
        </FormProvider>
      </Body>
    </ModalDialog>
  );
};
